import React from "react";

interface State {
  view?: string;
  data?: any;
  onConfirm?: any;
  onCancel?: any;
  isOpen: boolean;
}
type Action =
  | { type: "open"; view?: string; payload?: any, options?: OpenOptions }
  | { type: "close"; payload?: any };

type OpenOptions = {
  confirmCallback?: any,
  cancelCallback?: any
}

const initialState: State = {
  view: "default",
  isOpen: false,
  data: null,
  onConfirm: null,
  onCancel: null,
};

function modalReducer(state: State, action: Action): State {
  switch (action.type) {
    case "open":
      return {
        ...state,
        view: action.view,
        data: action.payload,
        isOpen: true,
        onConfirm: action.options?.confirmCallback,
        onCancel: action.options?.cancelCallback,
      };
    case "close":
      return {
        ...state,
        view: "default",
        data: action.payload,
        isOpen: false,
        onConfirm: null,
        onCancel: null,
      };
    default:
      throw new Error("Unknown Modal Action!");
  }
}

const ModalStateContext = React.createContext<State>(initialState);
ModalStateContext.displayName = "ModalStateContext";
const ModalActionContext =
  React.createContext<React.Dispatch<Action> | undefined>(undefined);
ModalActionContext.displayName = "ModalActionContext";

export const ModalProvider: React.FC = ({ children }) => {
  const [state, dispatch] = React.useReducer(modalReducer, initialState);
  return (
    <ModalStateContext.Provider value={state}>
      <ModalActionContext.Provider value={dispatch}>
        {children}
      </ModalActionContext.Provider>
    </ModalStateContext.Provider>
  );
};

export function useModalState() {
  const context = React.useContext(ModalStateContext);
  if (context === undefined) {
    throw new Error(`useModalState must be used within a ModalProvider`);
  }
  return context;
}

export function useModalAction() {
  const dispatch = React.useContext(ModalActionContext);
  if (dispatch === undefined) {
    throw new Error(`useModalAction must be used within a ModalProvider`);
  }
  return {
    openModal(view?: string, payload?: unknown, options?: OpenOptions) {
      dispatch({ type: "open", view, payload, options });
    },
    closeModal(payload?: unknown) {
      dispatch({ type: "close", payload });
    },
  };
}
